/**
 * BookingDrawer — slide-in 480px panel showing full booking details.
 *
 * Props:
 *   booking       – raw backend Booking object (null = closed)
 *   facilityName  – human-readable name of the facility
 *   slotTotal     – computed price for this booking (0 if unknown)
 *   api           – pre-configured axios instance from useAuth()
 *   onClose       – called when the overlay or ✕ is clicked
 *   onMutated     – called after any successful mutation (cancel, payment, etc.)
 */
import { useState } from 'react';
import { X, Phone } from 'lucide-react';
import { toast } from 'sonner';

/* ── Avatar helpers ──────────────────────────────────────────────────────────*/
const AV_COLORS = ['c0', 'c1', 'c2', 'c3', 'c4'];
function initials(name = '') {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}
function avc(name = '') {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 5;
  return AV_COLORS[h];
}

/* A slot_code part ("0900" → "09:00"; legacy "09" → "09:00") to wall-clock time. */
function codeToTime(t) {
  t = String(t || '');
  if (t.length >= 4) return `${t.slice(0, 2)}:${t.slice(2, 4)}`;
  if (t.length > 0) return `${t.padStart(2, '0')}:00`;
  return '';
}

/* Group a session's bookings by court, merging back-to-back hours into ranges
   (drawer Slots section · reference §6.4). */
function mergeDrawerSlots(slots) {
  const byCourt = {};
  for (const b of slots) {
    const [a, z] = (b.slot_id || '').split('-');
    const court = b.ground_id === '__rental__' ? 'Rental' : b.ground_id;
    (byCourt[court] = byCourt[court] || []).push({ start: codeToTime(a), end: codeToTime(z) });
  }
  return Object.keys(byCourt).map(court => {
    const sorted = byCourt[court].slice().sort((x, y) => x.start.localeCompare(y.start));
    const ranges = [];
    for (const s of sorted) {
      const last = ranges[ranges.length - 1];
      if (last && last.end === s.start) last.end = s.end;
      else ranges.push({ start: s.start, end: s.end });
    }
    return { court, ranges, count: sorted.length };
  });
}

/* ── Refund-window detection ─────────────────────────────────────────────────
   Within 24 hours of the booking start = NOT eligible.
   We use the booking date + slot start hour for the comparison.           */
function withinRefundWindow(booking) {
  try {
    const startT = codeToTime((booking.slot_id || '').split('-')[0]);
    if (!startT) return false;
    const start = new Date(`${booking.date}T${startT}:00+05:30`);
    const now = Date.now();
    const diff = start.getTime() - now;
    return diff < 24 * 60 * 60 * 1000; // within 24h
  } catch { return false; }
}

/* ── Cancel dialog ───────────────────────────────────────────────────────────*/
function CancelDialog({ booking, slotTotal, api, onClose, onDone }) {
  const inWindow = withinRefundWindow(booking);
  const paid = parseFloat(booking.payment?.amount) || 0;
  const hasPaid = paid > 0;
  // Can only be refund-eligible if outside 24h window AND payment was made
  const canBeEligible = !inWindow && hasPaid;
  const [eligible, setEligible] = useState(canBeEligible);
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await api.patch(`/api/bookings/${booking.booking_id}/cancel`, {
        refund_eligible: eligible,
      });
      toast.success('Booking cancelled');
      onDone();
      onClose();
    } catch {
      toast.error('Failed to cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  const outstanding = slotTotal > 0 ? Math.max(0, slotTotal - paid) : 0;

  return (
    <>
      <div className="sb-overlay" onClick={onClose} style={{ zIndex: 110 }} />
      <div className="sb-modal" style={{ width: 460, zIndex: 111 }}>
        <div className="sb-modal-head">
          <div style={{ fontSize: 11, color: 'var(--bad-fg)', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 6 }}>
            Cancel booking
          </div>
          <div style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-.015em', color: 'var(--ink)' }}>
            {booking.customer_name}
          </div>
          <div style={{ fontFamily: 'var(--f-mono)', fontSize: 12, color: 'var(--ink-3)', marginTop: 3 }}>
            {booking.date} · {booking.slot_time}
          </div>
        </div>
        <div className="sb-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.5, margin: 0 }}>
            This will mark {booking.booking_type === 'court' ? 'the court slot' : 'the rental'} as cancelled
            and free it up for re-booking.
          </p>

          {/* Money summary */}
          {slotTotal > 0 && (
            <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--ink-3)' }}>Total</span>
                <span className="sb-mono" style={{ fontWeight: 500 }}>₹{slotTotal}</span>
              </div>
              {paid > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: 'var(--ink-3)' }}>Collected</span>
                  <span className="sb-mono" style={{ color: 'var(--ok-fg)', fontWeight: 500 }}>₹{paid}</span>
                </div>
              )}
              {outstanding > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: 'var(--ink-3)' }}>Outstanding (forgiven)</span>
                  <span className="sb-mono" style={{ color: 'var(--warn-fg)', fontWeight: 500 }}>₹{outstanding}</span>
                </div>
              )}
            </div>
          )}

          {/* Refund eligibility */}
          {hasPaid ? (
            <label style={{ display: 'flex', gap: 10, cursor: canBeEligible ? 'pointer' : 'default', alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={eligible}
                disabled={!canBeEligible}
                onChange={e => setEligible(e.target.checked)}
                style={{ marginTop: 2, accentColor: 'var(--brand)', flexShrink: 0 }}
              />
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: canBeEligible ? 'var(--ink)' : 'var(--ink-3)' }}>
                  Queue ₹{paid} for refund
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 3, lineHeight: 1.5 }}>
                  {inWindow
                    ? 'Refund not available — booking is within the 24-hour policy window.'
                    : 'Booking is outside the 24-hour window — refund is allowed.'}
                </div>
              </div>
            </label>
          ) : (
            <div style={{ padding: '10px 14px', background: 'var(--info-bg)', borderRadius: 9, fontSize: 13, color: 'var(--info-fg)' }}>
              No payment recorded — nothing to refund.
            </div>
          )}
        </div>
        <div className="sb-modal-foot">
          <button onClick={onClose} className="sb-btn ghost">Keep booking</button>
          <button
            onClick={handleCancel}
            disabled={cancelling}
            className="sb-btn danger"
          >
            {cancelling ? 'Cancelling…' : eligible ? 'Cancel & queue refund' : 'Cancel booking'}
          </button>
        </div>
      </div>
    </>
  );
}

/* ── Record payment dialog ───────────────────────────────────────────────────*/
function PaymentDialog({ booking, slotTotal, api, onClose, onDone }) {
  const paid = parseFloat(booking.payment?.amount) || 0;
  const due  = slotTotal > 0 ? Math.max(0, slotTotal - paid) : 0;
  const [method, setMethod] = useState(booking.payment?.method || 'Cash');
  const [amount, setAmount] = useState(due > 0 ? String(due) : '');
  const [txId, setTxId] = useState('');
  const [saving, setSaving] = useState(false);

  const collected = parseFloat(amount) || 0;
  const newTotal  = paid + collected;
  const remaining = slotTotal > 0 ? Math.max(0, slotTotal - newTotal) : 0;
  const overPays  = slotTotal > 0 && newTotal > slotTotal;
  const autoStatus = slotTotal > 0
    ? (newTotal >= slotTotal ? 'Paid' : 'Pending')
    : collected > 0 ? 'Paid' : 'Pending';

  const handleSave = async () => {
    if (!collected || collected <= 0) { toast.error('Enter an amount'); return; }
    if (overPays) { toast.error('Cannot exceed due amount'); return; }
    setSaving(true);
    try {
      await api.patch(`/api/bookings/${booking.booking_id}/payment`, {
        method,
        amount: newTotal,
        status: autoStatus,
        transaction_id: method === 'UPI' ? txId : null,
      });
      toast.success('Payment recorded');
      onDone();
      onClose();
    } catch {
      toast.error('Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="sb-overlay" onClick={onClose} style={{ zIndex: 110 }} />
      <div className="sb-modal" style={{ width: 450, zIndex: 111 }}>
        <div className="sb-modal-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div className="sb-modal-title">Record payment</div>
            <div className="sb-modal-sub">{booking.booking_id?.slice(0, 8).toUpperCase()} · {booking.customer_name} · {booking.date}</div>
          </div>
          <button className="sb-btn ghost icon" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="sb-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Money block */}
          {slotTotal > 0 && (
            <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--ink-3)' }}>Total</span>
                <span className="sb-mono" style={{ fontWeight: 500 }}>₹{slotTotal}</span>
              </div>
              {paid > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: 'var(--ink-3)' }}>Already collected</span>
                  <span className="sb-mono" style={{ color: 'var(--ok-fg)' }}>₹{paid}</span>
                </div>
              )}
              <hr style={{ margin: '4px 0', border: 0, borderTop: '1px solid var(--line-soft)' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 600 }}>
                <span style={{ color: 'var(--warn-fg)' }}>Total due</span>
                <span className="sb-mono" style={{ color: 'var(--warn-fg)' }}>₹{due}</span>
              </div>
            </div>
          )}

          {/* Quick fill */}
          {due > 0 && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" onClick={() => setAmount(String(due))} className="sb-btn ghost sm" style={{ flex: 1, justifyContent: 'center', color: 'var(--ok-fg)' }}>
                Full ₹{due}
              </button>
              <button type="button" onClick={() => setAmount(String(Math.ceil(due / 2)))} className="sb-btn ghost sm" style={{ flex: 1, justifyContent: 'center' }}>
                Half ₹{Math.ceil(due / 2)}
              </button>
            </div>
          )}

          {/* Method chipset */}
          <div className="sb-field">
            <label>Method</label>
            <div className="sb-chipset" style={{ width: '100%', display: 'flex' }}>
              {['Cash', 'UPI', 'Card'].map(m => (
                <div key={m} className={`sb-chip ${method === m ? 'active' : ''}`}
                  onClick={() => setMethod(m)} style={{ flex: 1, textAlign: 'center' }}>
                  {m}
                </div>
              ))}
            </div>
          </div>

          {/* Amount */}
          <div className="sb-field">
            <label>Amount (₹)</label>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" autoFocus min={0} />
          </div>

          {/* UPI tx ID */}
          {method === 'UPI' && (
            <div className="sb-field">
              <label>Transaction ID</label>
              <input type="text" value={txId} onChange={e => setTxId(e.target.value)} placeholder="e.g. 3289xxxx" />
            </div>
          )}

          {/* Live banner */}
          {collected > 0 && (
            <div style={{
              padding: '10px 14px',
              borderRadius: 9,
              fontSize: 13,
              background: overPays ? 'var(--bad-bg)' : remaining === 0 ? 'var(--ok-bg)' : 'var(--warn-bg)',
              color: overPays ? 'var(--bad-fg)' : remaining === 0 ? 'var(--ok-fg)' : 'var(--warn-fg)',
            }}>
              {overPays
                ? `Cannot exceed ₹${due}.`
                : remaining === 0
                  ? 'Marks booking as fully paid.'
                  : `Partial — ₹${remaining} will remain due.`}
            </div>
          )}
        </div>
        <div className="sb-modal-foot">
          <button onClick={onClose} className="sb-btn ghost">Cancel</button>
          <button onClick={handleSave} disabled={saving || overPays} className="sb-btn brand">
            {saving ? 'Saving…' : 'Record payment'}
          </button>
        </div>
      </div>
    </>
  );
}

/* ── Main drawer ─────────────────────────────────────────────────────────────*/
export default function BookingDrawer({ booking, group, facilityName, slotTotal = 0, api, onClose, onMutated, onEdit }) {
  const [dialog, setDialog] = useState(null); // 'cancel' | 'payment'
  const [restoring, setRestoring] = useState(false);
  const [refunding, setRefunding] = useState(false);

  if (!booking) return null;

  // Slots in this session (drawer may be opened for a single booking or a group)
  const sessionSlots = group && group.length ? group : [booking];
  const mergedSlots = mergeDrawerSlots(sessionSlots);

  // Past = every slot in the session has already ended (IST). Cancel is hidden for past bookings.
  const slotEnded = (b) => {
    const endT = codeToTime((b.slot_id || '').split('-')[1]);
    if (!endT) return false;
    const end = new Date(`${b.date}T${endT}:00+05:30`);
    return end.getTime() <= Date.now();
  };
  const isPast = sessionSlots.every(slotEnded);

  const paid    = parseFloat(booking.payment?.amount) || 0;
  const due     = slotTotal > 0 ? Math.max(0, slotTotal - paid) : 0;
  const status  = booking.status;
  const ini     = initials(booking.customer_name);
  const colour  = avc(booking.customer_name);

  // Identify the booking type from id prefix or field
  const isRental = booking.booking_type === 'rental';

  const handleRestore = async () => {
    setRestoring(true);
    try {
      await api.patch(`/api/bookings/${booking.booking_id}/restore`);
      toast.success('Booking restored');
      onMutated();
      onClose();
    } catch {
      toast.error('Failed to restore booking');
    } finally {
      setRestoring(false);
    }
  };

  const handleMarkRefunded = async () => {
    setRefunding(true);
    try {
      await api.patch(`/api/bookings/${booking.booking_id}/refund-status`, { refund_processed: true });
      toast.success('Marked as refunded');
      onMutated();
      onClose();
    } catch {
      toast.error('Failed to update refund status');
    } finally {
      setRefunding(false);
    }
  };

  const handleUnmarkRefunded = async () => {
    setRefunding(true);
    try {
      await api.patch(`/api/bookings/${booking.booking_id}/refund-status`, { refund_processed: false });
      toast.success('Refund unmarked');
      onMutated();
      onClose();
    } catch {
      toast.error('Failed to update refund status');
    } finally {
      setRefunding(false);
    }
  };

  // Payment status badge
  const payStatus = booking.payment?.status || 'Pending';

  // Format created_at nicely
  let createdStr = '';
  try {
    createdStr = new Date(booking.created_at).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { createdStr = booking.created_at || ''; }

  return (
    <>
      <div className="sb-drawer-overlay" onClick={onClose} />
      <aside className="sb-drawer-panel sb-admin">
        {/* Head */}
        <div className="sb-drawer-head">
          <div
            className={`sb-av ${colour}`}
            style={{ width: 42, height: 42, fontSize: 15, flexShrink: 0 }}
          >
            {ini}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sb-drawer-id">{booking.booking_id?.slice(0, 8).toUpperCase()}</div>
            <div className="sb-drawer-name">{booking.customer_name}</div>
            <div className="sb-drawer-pills">
              <span className={`sb-badge ${status === 'confirmed' ? 'ok' : 'bad'}`}>
                {status === 'confirmed' ? 'Confirmed' : 'Cancelled'}
              </span>
              {isRental && (
                <span className="sb-badge info">Rental only</span>
              )}
              {status === 'cancelled' && booking.refund_eligible && !booking.refund_processed && (
                <span className="sb-badge warn">Refund pending · ₹{paid}</span>
              )}
              {status === 'cancelled' && booking.refund_processed && (
                <span className="sb-badge ok">Refunded</span>
              )}
            </div>
          </div>
          <button className="sb-btn ghost icon" onClick={onClose} style={{ flexShrink: 0 }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="sb-drawer-body">
          {/* 2×2 meta grid */}
          <div>
            <div className="sb-drawer-section-lbl">Details</div>
            <div className="sb-drawer-meta-grid">
              <div className="sb-drawer-meta-cell">
                <div className="sb-drawer-meta-label">When</div>
                <div className="sb-drawer-meta-val">{booking.date}</div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--f-mono)', marginTop: 2 }}>
                  {booking.slot_time}
                </div>
              </div>
              <div className="sb-drawer-meta-cell">
                <div className="sb-drawer-meta-label">Facility</div>
                <div className="sb-drawer-meta-val">{facilityName || '—'}</div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{booking.ground_id}</div>
              </div>
              <div className="sb-drawer-meta-cell">
                <div className="sb-drawer-meta-label">Phone</div>
                <div className="sb-drawer-meta-val" style={{ fontFamily: 'var(--f-mono)', fontSize: 13 }}>
                  {booking.customer_phone}
                </div>
                <a
                  href={`tel:${booking.customer_phone}`}
                  style={{ fontSize: 11, color: 'var(--brand)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 3, textDecoration: 'none' }}
                >
                  <Phone size={10} /> Call
                </a>
              </div>
              <div className="sb-drawer-meta-cell">
                <div className="sb-drawer-meta-label">Total</div>
                <div className="sb-drawer-meta-val" style={{ fontFamily: 'var(--f-mono)' }}>
                  {slotTotal > 0 ? `₹${slotTotal}` : '—'}
                </div>
                {paid > 0 && (
                  <div style={{ fontSize: 12, color: due === 0 ? 'var(--ok-fg)' : 'var(--warn-fg)', marginTop: 2 }}>
                    {due === 0 ? '✓ Paid in full' : `₹${due} due`}
                  </div>
                )}
                {paid === 0 && slotTotal > 0 && (
                  <div style={{ fontSize: 12, color: 'var(--warn-fg)', marginTop: 2 }}>Unpaid</div>
                )}
              </div>
            </div>
          </div>

          {/* Slots (merged ranges per court) */}
          {!isRental && (
            <div>
              <div className="sb-drawer-section-lbl">
                {sessionSlots.length > 1 ? `Slots · ${sessionSlots.length} sessions` : 'Slot'}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {mergedSlots.map((g, i) => {
                  const nonContiguous = g.ranges.length > 1;
                  return (
                    <div key={i} className="sb-drawer-slot-card">
                      <span className="sb-drawer-slot-court">{g.court}</span>
                      <span className="sb-drawer-slot-time">
                        {g.ranges.map((r, j) => (
                          <span key={j}>{j > 0 ? ', ' : ''}{r.start}–{r.end}</span>
                        ))}
                        {nonContiguous && (
                          <span className="sb-badge plain" style={{ marginLeft: 6, color: 'var(--info-fg)', borderColor: 'var(--info-bg)' }}>multi-time</span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Payment detail */}
          {(booking.payment?.method || payStatus !== 'Pending') && (
            <div>
              <div className="sb-drawer-section-lbl">Payment</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span className={`sb-badge ${payStatus === 'Paid' ? 'ok' : payStatus === 'Refunded' ? 'info' : 'warn'}`}>
                  {payStatus}
                </span>
                {booking.payment?.method && (
                  <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>via {booking.payment.method}</span>
                )}
                {booking.payment?.amount && (
                  <span className="sb-mono" style={{ fontWeight: 600, fontSize: 14 }}>₹{booking.payment.amount}</span>
                )}
                {booking.payment?.transaction_id && (
                  <span className="sb-mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>TXN: {booking.payment.transaction_id}</span>
                )}
              </div>
            </div>
          )}

          {/* Rentals */}
          {booking.rentals?.length > 0 && (
            <div>
              <div className="sb-drawer-section-lbl">Equipment rented</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {booking.rentals.map((r, i) => (
                  <div key={i} className="sb-drawer-slot-card">
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>
                      {r.name} × {r.quantity}
                    </span>
                    <span className="sb-mono" style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                      ₹{r.total}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Note */}
          {booking.notes && (
            <div className="sb-drawer-note">
              ✱ {booking.notes}
            </div>
          )}

          {/* Activity timeline */}
          <div>
            <div className="sb-drawer-section-lbl">Activity</div>
            <div className="sb-drawer-timeline">
              <div className="sb-drawer-timeline-item">
                <div className="sb-drawer-timeline-dot" />
                <div className="sb-drawer-timeline-content">
                  <div className="sb-drawer-timeline-event">Booking created</div>
                  <div className="sb-drawer-timeline-time">{createdStr}</div>
                </div>
              </div>
              {booking.payment?.amount && booking.payment.amount > 0 && (
                <div className="sb-drawer-timeline-item">
                  <div className="sb-drawer-timeline-dot ok" />
                  <div className="sb-drawer-timeline-content">
                    <div className="sb-drawer-timeline-event">
                      Payment collected — ₹{booking.payment.amount} via {booking.payment.method || 'unknown'}
                    </div>
                    <div className="sb-drawer-timeline-time">{createdStr}</div>
                  </div>
                </div>
              )}
              {status === 'cancelled' && (
                <div className="sb-drawer-timeline-item">
                  <div className="sb-drawer-timeline-dot bad" />
                  <div className="sb-drawer-timeline-content">
                    <div className="sb-drawer-timeline-event">Booking cancelled</div>
                    {booking.cancelled_at && (
                      <div className="sb-drawer-timeline-time">
                        {new Date(booking.cancelled_at).toLocaleString('en-IN', {
                          timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {booking.refund_processed && (
                <div className="sb-drawer-timeline-item">
                  <div className="sb-drawer-timeline-dot ok" />
                  <div className="sb-drawer-timeline-content">
                    <div className="sb-drawer-timeline-event">Refund issued — ₹{paid}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer action bar */}
        <div className="sb-drawer-foot">
          {status === 'confirmed' && (
            <>
              {!isPast && (
                <button
                  className="sb-btn"
                  style={{ color: 'var(--bad-fg)', borderColor: 'var(--bad-fg)' }}
                  onClick={() => setDialog('cancel')}
                >
                  Cancel booking
                </button>
              )}
              {onEdit && (
                <button className="sb-btn" onClick={() => onEdit(booking, sessionSlots)}>
                  Edit
                </button>
              )}
              {due > 0 && (
                <button className="sb-btn brand" style={{ marginLeft: 'auto' }} onClick={() => setDialog('payment')}>
                  Record ₹{due}
                </button>
              )}
            </>
          )}

          {status === 'cancelled' && booking.refund_eligible && !booking.refund_processed && (
            <>
              <button className="sb-btn ghost sm" disabled={restoring} onClick={handleRestore}>
                {restoring ? 'Restoring…' : 'Restore booking'}
              </button>
              <button className="sb-btn brand" style={{ marginLeft: 'auto' }} disabled={refunding} onClick={handleMarkRefunded}>
                {refunding ? '…' : `Mark refunded · ₹${paid}`}
              </button>
            </>
          )}

          {status === 'cancelled' && !booking.refund_eligible && !booking.refund_processed && (
            <button className="sb-btn ghost sm" disabled={restoring} onClick={handleRestore}>
              {restoring ? 'Restoring…' : 'Restore booking'}
            </button>
          )}

          {status === 'cancelled' && booking.refund_processed && (
            <>
              <span className="sb-badge ok" style={{ marginRight: 4 }}>✓ Refund completed</span>
              <button className="sb-btn ghost sm" disabled={refunding} onClick={handleUnmarkRefunded}>
                {refunding ? '…' : 'Unmark as refunded'}
              </button>
            </>
          )}
        </div>
      </aside>

      {/* Sub-dialogs rendered above the drawer */}
      {dialog === 'cancel' && (
        <CancelDialog
          booking={booking}
          slotTotal={slotTotal}
          api={api}
          onClose={() => setDialog(null)}
          onDone={() => { onMutated(); onClose(); }}
        />
      )}
      {dialog === 'payment' && (
        <PaymentDialog
          booking={booking}
          slotTotal={slotTotal}
          api={api}
          onClose={() => setDialog(null)}
          onDone={onMutated}
        />
      )}
    </>
  );
}
